/* ═══════════════════════════════════════════════════════════════════════════════
   WATRLY CUSTOMER HOMEPAGE — الصفحة الرئيسية للعميل
   بتصميم مائي زجاجي مذهل — Water Glass Design System
   ═══════════════════════════════════════════════════════════════════════════════ */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import SAR from '../../../components/SAR';
import { useToast } from '../../context/ToastContext';
import { useDialog } from '../../../context/DialogContext';

// Import Water Design System Components
import {
  WaterHeroBanner,
  WaterMiniBanner,
  WaterActiveOrderBanner,
  WaterStatsBanner,
} from '../../components/WaterBanners';

import {
  WaterButton,
  WaterActionCard,
  WaterLink,
} from '../../components/WaterButtons';

import {
  DropletIcon,
  WaveIcon,
  WaterTruckIcon,
  WaterHomeIcon,
  WaterListIcon,
  WaterProfileIcon,
  WaterPinIcon,
  WaterWalletIcon,
  WaterPlusIcon,
  WaterCheckIcon,
  WaterCloseIcon,
  WaterSearchIcon,
  WaterChevronLeftIcon,
  WaterChevronRightIcon,
  WaterClockIcon,
} from '../../components/WaterIcons';

const WS_BASE = process.env.NODE_ENV === 'production'
  ? `wss://${window.location.host}/api/v1`
  : 'ws://localhost:8002/api/v1';

const ACTIVE_STATUSES = ['pending','confirmed','assigned','accepted','started','en_route_pickup','at_source','loading','en_route_delivery','arrived','delivering','no_driver_available','collecting_offers','offers_ready','expired_no_offers'];

const STATUS_META = {
  pending:             { label: 'في الانتظار',         color: 'warning',    icon: WaterClockIcon },
  confirmed:           { label: 'مؤكد',               color: 'info',       icon: WaterCheckIcon },
  no_driver_available: { label: 'جارٍ البحث عن سائق', color: 'warning',    icon: WaterSearchIcon },
  assigned:            { label: 'تم تعيين سائق',       color: 'active',     icon: WaterTruckIcon },
  accepted:            { label: 'السائق في الطريق',    color: 'active',     icon: WaterTruckIcon },
  started:             { label: 'جارٍ التوصيل',        color: 'active',     icon: WaterTruckIcon },
  en_route_pickup:     { label: 'السائق في الطريق',    color: 'active',     icon: WaterTruckIcon },
  at_source:           { label: 'السائق عند المصدر',   color: 'active',     icon: WaveIcon },
  loading:             { label: 'جارٍ التعبئة',        color: 'active',     icon: DropletIcon },
  en_route_delivery:   { label: 'في طريقه إليك',       color: 'active',     icon: WaterTruckIcon },
  arrived:             { label: 'وصل السائق',          color: 'active',     icon: WaterPinIcon },
  delivering:          { label: 'جارٍ التسليم',        color: 'active',     icon: DropletIcon },
  completed:           { label: 'مكتمل',               color: 'success',    icon: WaterCheckIcon },
  cancelled:           { label: 'ملغى',                color: 'error',      icon: WaterCloseIcon },
  collecting_offers:   { label: 'جمع العروض',          color: 'warning',    icon: WaterSearchIcon },
  offers_ready:        { label: 'عروض جاهزة',          color: 'info',       icon: WaterCheckIcon },
  expired_no_offers:   { label: 'انتهت المدة',         color: 'error',      icon: WaterCloseIcon },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  
  const colorStyles = {
    warning: 'bg-[rgba(251,191,36,0.12)] text-[#fbbf24] border-[rgba(251,191,36,0.3)]',
    info: 'bg-[rgba(10,186,181,0.12)] text-[#0ABAB5] border-[rgba(10,186,181,0.3)]',
    active: 'bg-[rgba(0,188,212,0.12)] text-[#00BCD4] border-[rgba(0,188,212,0.3)] animate-pulse',
    success: 'bg-[rgba(0,212,170,0.12)] text-[#00D4AA] border-[rgba(0,212,170,0.3)]',
    error: 'bg-[rgba(244,63,94,0.12)] text-[#f43f5e] border-[rgba(244,63,94,0.3)]',
  };

  return (
    <span className={`
      inline-flex items-center gap-1.5
      px-3 py-1 rounded-full text-xs font-bold
      border ${colorStyles[meta.color]}
    `}>
      <meta.icon className="w-3.5 h-3.5" />
      {meta.label}
    </span>
  );
};

export default function HomePage() {
  const { customer, apiCall, getToken } = useCustomerAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm: dialog } = useDialog();
  const [stats,        setStats]       = useState(null);
  const [recent,       setRecent]      = useState([]);
  const [activeOrder,  setActiveOrder] = useState(null);
  const [activeOrders, setActiveOrders] = useState([]);
  const [loading,      setLoading]     = useState(true);
  const wsRef = useRef(null);

  const cancelOrder = useCallback(async (orderId) => {
    if (!await dialog({ title: 'إلغاء الطلب', message: 'هل أنت متأكد من إلغاء هذا الطلب؟', type: 'warning', confirmText: 'إلغاء الطلب', cancelText: 'تراجع' })) return;
    try {
      await apiCall(`/customer/orders/${orderId}/cancel`, { method: 'POST' });
      toast.success('تم إلغاء الطلب');
      setActiveOrders(prev => prev.filter(o => o.id !== orderId));
      setActiveOrder(prev => prev?.id === orderId ? null : prev);
    } catch (err) { toast.error(err.message || 'تعذّر الإلغاء'); }
  }, [apiCall, dialog, toast]);

  useEffect(() => {
    const load = async () => {
      try {
        const o = await apiCall('/customer/orders?limit=100');
        const orders = o.orders || [];
        const completed = orders.filter(x => x.status === 'completed');
        setStats({
          total_orders: orders.filter(x => x.status !== 'cancelled').length,
          completed:    completed.length,
          active:       orders.filter(x => ACTIVE_STATUSES.includes(x.status)).length,
          cancelled:    orders.filter(x => x.status === 'cancelled').length,
          total_spent:  completed.reduce((s, x) => s + (x.actual_total || x.estimated_total || 0), 0),
        });
        setRecent(orders.slice(0, 3));
        const actives = orders.filter(ord => ACTIVE_STATUSES.includes(ord.status));
        setActiveOrders(actives);
        setActiveOrder(actives[0] || null);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    load();
  }, [apiCall]);

  // ── Real-time WebSocket ──
  useEffect(() => {
    if (!customer?.id) return;
    const token = getToken();
    const url = `${WS_BASE}/ws/customer/${customer.id}?token=${token || ''}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
    }, 25000);

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type !== 'order_status') return;
        const { order_id, status } = msg;

        // Update activeOrders list
        setActiveOrders(prev => {
          const updated = prev.map(o => o.id === order_id ? { ...o, status } : o);
          return updated.filter(o => ACTIVE_STATUSES.includes(o.status));
        });

        // Update activeOrder (legacy single ref)
        setActiveOrder(prev => {
          if (!prev) return prev;
          if (prev.id !== order_id) return prev;
          if (!ACTIVE_STATUSES.includes(status)) return null;
          return { ...prev, status };
        });

        // Update recent list
        setRecent(prev =>
          prev.map(o => o.id === order_id ? { ...o, status } : o)
        );

        // Update active count in stats
        setStats(prev => {
          if (!prev) return prev;
          // re-derive active from current activeOrder state isn't reliable here;
          // just nudge active count: +1 if became active, -1 if finished
          const wasActive = ACTIVE_STATUSES.includes(status);
          const nowDone = status === 'completed';
          return {
            ...prev,
            active:    wasActive ? prev.active : Math.max(0, prev.active - 1),
            completed: nowDone   ? prev.completed + 1 : prev.completed,
          };
        });
      } catch { /* ignore bad JSON */ }
    };

    ws.onerror = () => {};

    return () => {
      clearInterval(ping);
      ws.close();
    };
  }, [customer?.id, getToken]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'صباح الخير' : hour < 17 ? 'مساء الخير' : 'مساء النور';

  return (
    <div className="flex flex-col gap-5 animate-fade-in">
      
      {/* ══ PROMO SLIDER ══ */}
      <PromoSlider onOrder={() => navigate('/customer/orders/new')} />

      {/* ══ ACTIVE ORDERS ══ */}
      {activeOrders.length > 0 && (
        <WaterActiveOrderBanner
          orders={activeOrders.map(o => ({
            id: o.id,
            orderNumber: o.order_number,
            status: o.status,
            statusLabel: STATUS_META[o.status]?.label || 'نشط',
            details: `${o.water_type || 'مياه'} — ${o.requested_quantity || o.quantity} طن`,
          }))}
          onOrderClick={(id) => navigate(`/customer/orders/${id}`)}
          onCancel={cancelOrder}
          className="animate-drop-in"
        />
      )}

      {/* ══ NEW ORDER CARD ══ */}
      <div className="flex justify-center mt-2">
        <NewOrderCard onClick={() => navigate('/customer/orders/new')} />
      </div>


      {/* ══ EMPTY STATE ══ */}
      {!loading && recent.length === 0 && (
        <div className="relative overflow-hidden rounded-3xl p-8 text-center bg-[rgba(255,255,255,0.05)] backdrop-blur-xl border border-[rgba(255,255,255,0.1)] animate-drop-in">
          {/* Floating droplet */}
          <div className="w-20 h-20 rounded-full mx-auto mb-4 bg-gradient-to-br from-[rgba(10,186,181,0.2)] to-[rgba(0,188,212,0.1)] border border-[rgba(10,186,181,0.25)] flex items-center justify-center animate-float">
            <DropletIcon className="w-10 h-10 text-[#0ABAB5]/60" />
          </div>
          <p className="text-white/90 font-bold text-lg mb-2">لا توجد طلبات بعد</p>
          <p className="text-white/50 text-sm mb-5">ابدأ تجربتك مع وترلي الآن</p>
          <WaterButton
            variant="primary"
            icon={WaterPlusIcon}
            onClick={() => navigate('/customer/orders/new')}
          >
            طلب جديد
          </WaterButton>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   Helper Components
   ═══════════════════════════════════════════════════════════════════════════════ */
function SectionTitle({ children }) {
  return (
    <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-2.5">
      {children}
    </p>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   NewOrderCard — بطاقة طلب جديد بتأثيرات مائية
   ═══════════════════════════════════════════════════════════════════════════════ */
function NewOrderCard({ onClick }) {
  const [splashing, setSplashing] = useState(false);
  const [drops, setDrops] = useState([]);
  const [waving, setWaving] = useState(false);
  const cardRef = useRef(null);
  const dropId = useRef(0);

  const handleClick = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    const x = e.clientX - (rect?.left ?? 0);
    const y = e.clientY - (rect?.top ?? 0);

    // توليد قطرات عشوائية
    const newDrops = Array.from({ length: 12 }, (_, i) => ({
      id: dropId.current++,
      x,
      y,
      angle: (i / 12) * 360,
      size: 4 + Math.random() * 8,
      dist: 40 + Math.random() * 80,
      duration: 0.5 + Math.random() * 0.4,
    }));
    setDrops(prev => [...prev, ...newDrops]);
    setSplashing(true);
    setWaving(true);

    setTimeout(() => setSplashing(false), 300);
    setTimeout(() => setWaving(false), 700);
    setTimeout(() => setDrops(prev => prev.filter(d => !newDrops.find(nd => nd.id === d.id))), 900);

    onClick();
  };

  return (
    <div
      ref={cardRef}
      onClick={handleClick}
      className="relative overflow-hidden rounded-3xl cursor-pointer select-none"
      style={{ perspective: '600px', width: 260 }}
    >
      {/* طبقة الخلفية الأساسية */}
      <div className={`
        relative overflow-hidden rounded-3xl
        transition-transform duration-300
        ${splashing ? 'scale-[0.97]' : 'scale-100'}
      `}
        style={{
          background: 'linear-gradient(135deg, #0a2540 0%, #0d3060 40%, #0ABAB5 100%)',
          transformStyle: 'preserve-3d',
        }}
      >
        {/* موجة علوية متحركة */}
        <div
          className="absolute top-0 inset-x-0 h-16 overflow-hidden pointer-events-none"
          style={{
            transform: waving ? 'translateY(-4px)' : 'translateY(0)',
            transition: 'transform 0.6s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        >
          <svg viewBox="0 0 400 60" preserveAspectRatio="none" className="w-full h-full opacity-30">
            <path d="M0,30 C60,10 120,50 180,30 C240,10 300,50 360,30 C390,18 400,25 400,25 L400,0 L0,0 Z"
              fill="#22d3ee" className="animate-wave-slow"/>
            <path d="M0,35 C80,15 160,55 240,35 C320,15 380,45 400,35 L400,0 L0,0 Z"
              fill="#0ABAB5" opacity="0.5" className="animate-wave-fast"/>
          </svg>
        </div>

        {/* موجة سفلية متحركة */}
        <div
          className="absolute bottom-0 inset-x-0 h-16 overflow-hidden pointer-events-none"
          style={{
            transform: waving ? 'translateY(4px)' : 'translateY(0)',
            transition: 'transform 0.6s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        >
          <svg viewBox="0 0 400 60" preserveAspectRatio="none" className="w-full h-full opacity-30">
            <path d="M0,30 C60,50 120,10 180,30 C240,50 300,10 360,30 C390,42 400,35 400,35 L400,60 L0,60 Z"
              fill="#22d3ee" className="animate-wave-slow"/>
            <path d="M0,25 C80,45 160,5 240,25 C320,45 380,15 400,25 L400,60 L0,60 Z"
              fill="#0ABAB5" opacity="0.5" className="animate-wave-fast"/>
          </svg>
        </div>

        {/* فقاعات عائمة خلفية */}
        {[
          { w:60,  h:60,  top:'10%',  left:'5%',   delay:'0s',   op:0.06 },
          { w:90,  h:90,  top:'50%',  left:'15%',  delay:'1s',   op:0.05 },
          { w:45,  h:45,  top:'20%',  left:'80%',  delay:'0.5s', op:0.07 },
          { w:70,  h:70,  top:'65%',  left:'70%',  delay:'1.5s', op:0.05 },
          { w:30,  h:30,  top:'40%',  left:'50%',  delay:'0.8s', op:0.08 },
        ].map((b, i) => (
          <div key={i} className="absolute rounded-full border border-white animate-float pointer-events-none"
            style={{ width:b.w, height:b.h, top:b.top, left:b.left, animationDelay:b.delay, opacity:b.op }} />
        ))}

        {/* توهج مركزي */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(34,211,238,0.15) 0%, transparent 70%)' }} />

        {/* محتوى البطاقة */}
        <div className="relative z-10 flex flex-col items-center justify-center py-5 px-5 text-center">
          {/* أيقونة القطرة المركزية */}
          <div className="relative mb-3">
            {/* حلقات نابضة */}
            <div className="absolute inset-0 rounded-full bg-[#0ABAB5] opacity-20 animate-ping" style={{ animationDuration: '2s' }} />
            <div className="absolute -inset-3 rounded-full border border-[#0ABAB5]/30 animate-pulse" />
            <div className="absolute -inset-6 rounded-full border border-[#0ABAB5]/15 animate-pulse" style={{ animationDelay: '0.5s' }} />

            {/* الدائرة الرئيسية */}
            <div className={`
              relative w-14 h-14 rounded-full flex items-center justify-center overflow-hidden
              transition-transform duration-300
              ${splashing ? 'scale-110' : 'scale-100'}
            `}
              style={{
                background: 'linear-gradient(135deg, rgba(10,186,181,0.9) 0%, rgba(0,102,204,0.8) 100%)',
                boxShadow: '0 0 30px rgba(10,186,181,0.6), 0 0 60px rgba(10,186,181,0.3), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              {/* بريق داخلي */}
              <div className="absolute top-2 left-3 w-4 h-4 rounded-full bg-white opacity-20 blur-sm" />
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2C8.5 2 5 5 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-4-3.5-7-7-7z"/>
                <circle cx="12" cy="9" r="2.5" fill="rgba(255,255,255,0.3)"/>
                <path d="M9 20h6M8 22h8" strokeOpacity="0.5"/>
              </svg>
            </div>
          </div>

          {/* النص */}
          <h2 className="text-white text-lg font-black tracking-tight mb-1"
            style={{ textShadow: '0 0 20px rgba(34,211,238,0.5)' }}>
            طلب جديد
          </h2>
          <p className="text-[#22d3ee]/70 text-xs font-medium mb-4">
            اطلب المياه النقية الآن · توصيل سريع
          </p>

          {/* زر النقر */}
          <div
            className={`
              inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm
              transition-all duration-200
              ${splashing ? 'scale-95 brightness-110' : 'scale-100'}
            `}
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)',
              border: '1.5px solid rgba(255,255,255,0.3)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
              color: 'white',
            }}
          >
            <span>اضغط للطلب</span>
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 opacity-80">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1.293-5.293a1 1 0 001.414 0l3-3a1 1 0 000-1.414l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 000 2h3.586l-1.879 1.879a1 1 0 000 1.414z" clipRule="evenodd"/>
            </svg>
          </div>
        </div>

        {/* حدود متوهجة */}
        <div className="absolute inset-0 rounded-3xl pointer-events-none"
          style={{ border: '1px solid rgba(34,211,238,0.2)', boxShadow: 'inset 0 0 60px rgba(10,186,181,0.08)' }} />
      </div>

      {/* قطرات تتطاير عند النقر */}
      {drops.map(drop => (
        <div
          key={drop.id}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: drop.x,
            top: drop.y,
            width: drop.size,
            height: drop.size,
            background: 'radial-gradient(circle, #22d3ee, #0ABAB5)',
            boxShadow: '0 0 6px rgba(34,211,238,0.8)',
            transform: 'translate(-50%, -50%) scale(1)',
            animation: `dropFly ${drop.duration}s ease-out forwards`,
            '--dx': `${Math.cos(drop.angle * Math.PI / 180) * drop.dist}px`,
            '--dy': `${Math.sin(drop.angle * Math.PI / 180) * drop.dist}px`,
          }}
        />
      ))}

      <style>{`
        @keyframes dropFly {
          0%   { transform: translate(-50%,-50%) scale(1); opacity: 1; }
          100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0); opacity: 0; }
        }
        @keyframes wave-slow {
          0%,100% { d: path("M0,30 C60,10 120,50 180,30 C240,10 300,50 360,30 C390,18 400,25 400,25 L400,0 L0,0 Z"); }
          50%      { d: path("M0,25 C60,45 120,10 180,25 C240,45 300,10 360,25 C390,35 400,30 400,30 L400,0 L0,0 Z"); }
        }
        .animate-wave-slow { animation: wave-slow 4s ease-in-out infinite; }
        .animate-wave-fast  { animation: wave-slow 2.5s ease-in-out infinite reverse; }
      `}</style>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PromoSlider — بانر إعلاني مع شرائح تلف وتأثيرات مائية
   ═══════════════════════════════════════════════════════════════════════════════ */
const _preloadTruck = new Image(); _preloadTruck.src = '/assets/images/sysIcons/water_care.svg';

const TRUCK_STYLES = {
  dark:  { filter: 'invert(60%) sepia(80%) saturate(400%) hue-rotate(150deg) brightness(1.3)', pin: '#0ABAB5' },
  light: { filter: 'brightness(0.55) saturate(1.2)', pin: '#0066CC' },
};

function TruckIllustration() {
  const [mode, setMode] = useState(() =>
    document.documentElement.getAttribute('data-theme') !== 'light' ? 'dark' : 'light'
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setMode(document.documentElement.getAttribute('data-theme') !== 'light' ? 'dark' : 'light')
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  const { filter: truckFilter, pin: pinColor } = TRUCK_STYLES[mode];
  return (
    <div className="relative w-32 h-28 flex-shrink-0">
      <style>{`
        @keyframes wt-truck-enter {
          0%   { transform: translateX(-48px); opacity: 0; }
          60%  { transform: translateX(6px);  opacity: 1; }
          80%  { transform: translateX(-3px); }
          100% { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
      <svg width="84" height="84" viewBox="0 0 24 24" fill="none" stroke="#0ABAB5" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
        className="absolute bottom-0 left-0"
        style={{ zIndex: 2, animation: 'wt-truck-enter 0.6s cubic-bezier(0.22,1,0.36,1) forwards' }}>
        <rect x="1" y="8" width="15" height="10" rx="1"/>
        <path d="M16 10h4l2 4v4h-6V10z"/>
        <circle cx="5.5" cy="18.5" r="1.5" fill="#0ABAB5"/>
        <circle cx="18.5" cy="18.5" r="1.5" fill="#0ABAB5"/>
        <path d="M1 12h15" strokeOpacity="0.4"/>
        <path d="M4 8V6a2 2 0 012-2h6a2 2 0 012 2v2" strokeOpacity="0.6"/>
      </svg>
      <svg viewBox="0 0 120 90" className="absolute inset-0 w-full h-full" style={{ opacity: 0.6, zIndex: 1 }}>
        <path d="M 62 75 L 78 75 Q 95 75 95 58 Q 95 38 108 38 Q 118 38 118 22 Q 118 8 105 6"
          fill="none" stroke="#0ABAB5" strokeWidth="2.5" strokeDasharray="5,4" strokeLinecap="round"/>
      </svg>
      <svg viewBox="0 0 24 24" fill={pinColor} className="absolute top-0 right-0" width="30" height="30"
        style={{ zIndex: 2, filter: mode === 'dark' ? 'drop-shadow(0 0 4px rgba(10,186,181,0.6))' : 'drop-shadow(0 2px 4px rgba(0,80,180,0.3))' }}>
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
      </svg>
    </div>
  );
}

const SLIDES = [
  {
    id: 1,
    tag: 'توصيل سريع',
    title: 'ناقلتك تجيك\nبسهولة',
    subtitle: 'اختر الخدمة اللي تحتاجها وتابعها من التطبيق',
    cta: 'اطلب الآن',
    gradient: null,
    accent: '#0ABAB5',
    illustration: <TruckIllustration />,
  },
  {
    id: 2,
    tag: 'مياه نقية',
    title: 'مياه عالية\nالجودة',
    subtitle: 'مصادر موثوقة ومعتمدة لضمان صحتك',
    cta: 'اكتشف الخيارات',
    gradient: null,
    accent: '#00D4AA',
    illustration: (
      <div className="relative w-24 h-20 flex-shrink-0 flex items-center justify-center">
        {/* قطرة كبيرة */}
        <svg viewBox="0 0 60 80" className="w-16 h-20 drop-shadow-xl">
          <defs>
            <linearGradient id="dropGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#00D4AA" stopOpacity="0.9"/>
              <stop offset="100%" stopColor="#0ABAB5" stopOpacity="0.7"/>
            </linearGradient>
          </defs>
          <path d="M30 5 C30 5 5 35 5 52 C5 66 16 75 30 75 C44 75 55 66 55 52 C55 35 30 5 30 5Z" fill="url(#dropGrad)"/>
          <path d="M20 45 C18 38 22 30 28 28" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.5"/>
          <circle cx="38" cy="35" r="3" fill="white" opacity="0.3"/>
        </svg>
        {/* قطرات صغيرة حول */}
        <div className="absolute top-0 right-0 w-3 h-3 rounded-full bg-[#00D4AA]/50 animate-bounce" style={{animationDelay:'0.2s'}}/>
        <div className="absolute top-4 left-1 w-2 h-2 rounded-full bg-[#0ABAB5]/40 animate-bounce" style={{animationDelay:'0.5s'}}/>
        <div className="absolute bottom-2 right-1 w-2 h-2 rounded-full bg-[#00D4AA]/30 animate-bounce" style={{animationDelay:'0.8s'}}/>
      </div>
    ),
  },
  {
    id: 3,
    tag: 'جودة عالية',
    title: 'مياه نقية\nومعتمدة',
    subtitle: 'مصادر موثوقة ومعايير جودة عالية',
    gradient: null,
    accent: '#29B6F6',
    illustration: (
      <div className="relative w-24 h-20 flex-shrink-0 flex items-center justify-center">
        <svg viewBox="0 0 70 70" className="w-16 h-16">
          <defs>
            <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#29B6F6"/>
              <stop offset="100%" stopColor="#0066CC"/>
            </linearGradient>
          </defs>
          <path d="M35 5 L58 16 L58 35 C58 50 47 62 35 66 C23 62 12 50 12 35 L12 16 Z" fill="url(#shieldGrad)" opacity="0.85"/>
          <path d="M35 10 L54 19 L54 35 C54 47 45 58 35 62 C25 58 16 47 16 35 L16 19 Z" fill="white" opacity="0.08"/>
          <path d="M35 20 C35 20 26 29 26 35 C26 40.5 30 45 35 45 C40 45 44 40.5 44 35 C44 29 35 20 35 20Z" fill="white" opacity="0.7"/>
          <path d="M30 34 C29 31 31 27 34 26" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.5"/>
        </svg>
        <div className="absolute top-0 right-1 w-3 h-3 rounded-full bg-[#29B6F6]/60 animate-bounce" style={{animationDelay:'0.3s'}}/>
        <div className="absolute bottom-1 left-1 w-2 h-2 rounded-full bg-[#29B6F6]/40 animate-bounce" style={{animationDelay:'0.7s'}}/>
      </div>
    ),
  },
];

function PromoSlider({ onOrder }) {
  const [current, setCurrent] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const timerRef = useRef(null);
  const total = SLIDES.length;

  const next = useCallback(() => setCurrent(c => (c + 1) % total), [total]);
  const prev = useCallback(() => setCurrent(c => (c - 1 + total) % total), [total]);

  useEffect(() => {
    timerRef.current = setInterval(next, 4000);
    return () => clearInterval(timerRef.current);
  }, [next]);

  const resetTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(next, 4000);
  };

  const onTouchStart = (e) => { setStartX(e.touches[0].clientX); setDragging(true); };
  const onTouchEnd   = (e) => {
    if (!dragging) return;
    const diff = startX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) { diff > 0 ? next() : prev(); resetTimer(); }
    setDragging(false);
  };
  const onMouseDown  = (e) => { setStartX(e.clientX); setDragging(true); };
  const onMouseUp    = (e) => {
    if (!dragging) return;
    const diff = startX - e.clientX;
    if (Math.abs(diff) > 40) { diff > 0 ? next() : prev(); resetTimer(); }
    setDragging(false);
  };

  const slide = SLIDES[current];

  return (
    <div
      className="relative overflow-hidden rounded-3xl select-none cursor-grab active:cursor-grabbing"
      style={{ height: 180 }}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}   onMouseUp={onMouseUp} onMouseLeave={() => setDragging(false)}
    >
      {/* خلفية متحركة */}
      <div
        className="absolute inset-0 transition-all duration-700"
        style={{ background: 'var(--wt-bg-2)' }}
      />

      {/* دوائر ضوئية خلفية */}
      <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full opacity-20 blur-2xl transition-all duration-700"
        style={{ background: slide.accent }} />
      <div className="absolute -bottom-10 -left-10 w-36 h-36 rounded-full opacity-15 blur-2xl transition-all duration-700"
        style={{ background: slide.accent }} />

      {/* قطرات عائمة ديكور */}
      <div className="absolute top-4 left-[30%] w-1.5 h-1.5 rounded-full opacity-30 animate-float"
        style={{ background: slide.accent, animationDelay: '0s' }} />
      <div className="absolute top-10 left-[50%] w-1 h-1 rounded-full opacity-20 animate-float"
        style={{ background: slide.accent, animationDelay: '0.7s' }} />
      <div className="absolute bottom-6 left-[40%] w-2 h-2 rounded-full opacity-20 animate-float"
        style={{ background: slide.accent, animationDelay: '1.2s' }} />
      <div className="absolute top-16 right-[35%] w-1 h-1 rounded-full opacity-25 animate-float"
        style={{ background: slide.accent, animationDelay: '0.4s' }} />

      {/* حدود متوهجة */}
      <div className="absolute inset-0 rounded-3xl pointer-events-none" style={{ border: '1px solid var(--wt-border)' }} />
      <div className="absolute inset-0 rounded-3xl pointer-events-none"
        style={{ boxShadow: `inset 0 0 40px ${slide.accent}18` }} />

      {/* المحتوى */}
      <div className="relative z-10 flex items-center justify-between gap-2 px-4 py-3 h-full">
        {/* نص */}
        <div className="flex-1 min-w-0">
          <span
            className="inline-block text-[9px] font-bold px-2 py-0.5 rounded-full mb-2 border"
            style={{ color: slide.accent, borderColor: `${slide.accent}40`, background: `${slide.accent}15` }}
          >
            {slide.tag}
          </span>
          <h2 className="text-base font-bold leading-snug mb-1 whitespace-pre-line" style={{ color: 'var(--wt-text-primary)' }}>
            {slide.title}
          </h2>
          <p className="text-[11px] leading-relaxed" style={{ color: 'var(--wt-text-muted)' }}>
            {slide.subtitle}
          </p>
        </div>

        {/* رسم */}
        <div className="flex-shrink-0">
          {slide.illustration}
        </div>
      </div>
    </div>
  );
}

